<template>
  <v-container>
    <v-row class="mb-6">
      <v-col cols="12" sm="6">
        <h1>Venues</h1>
        <p class="text-body2 text-medium-emphasis">
          Named locations — pick these when setting a gig's, call's, or position's venue,
          or just type a one-off location instead.
        </p>
      </v-col>
      <v-col cols="12" sm="6" class="text-right">
        <v-btn color="primary" @click="openCreate">
          <v-icon start>mdi-map-marker-plus</v-icon>
          Add Venue
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="store.error" type="error" closable class="mb-4" @click:close="store.error = null">
      {{ store.error }}
    </v-alert>

    <v-row v-if="store.isLoading">
      <v-col cols="12"><v-skeleton-loader type="table-tbody" /></v-col>
    </v-row>

    <v-row v-else-if="store.venues.length === 0">
      <v-col cols="12">
        <v-card>
          <v-card-text class="text-center py-12">
            <v-icon size="48" class="text-medium-emphasis mb-4">mdi-map-marker-outline</v-icon>
            <p class="text-h6">No venues yet</p>
            <p class="text-body2 text-medium-emphasis">Add the places you regularly work</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col v-for="venue in store.venues" :key="venue.id" cols="12" md="6" lg="4">
        <v-card class="h-100">
          <v-card-item>
            <template #title>{{ venue.name }}</template>
            <template #append>
              <v-btn icon="mdi-pencil" size="small" variant="text" color="primary" @click="openEdit(venue)" />
              <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="removeVenue(venue.id)" />
            </template>
          </v-card-item>
          <v-card-text>
            <p v-if="formatAddress(venue.address)" class="text-body2 text-medium-emphasis">
              {{ formatAddress(venue.address) }}
            </p>
            <p v-if="venue.notes" class="text-body2 text-medium-emphasis">{{ venue.notes }}</p>

            <div v-if="venue.rooms.length" class="mt-3">
              <p class="text-caption text-medium-emphasis mb-1">Rooms</p>
              <v-chip v-for="room in venue.rooms" :key="room.id" size="small" class="mr-1 mb-1">
                {{ room.name }}
              </v-chip>
            </div>

            <div v-if="venue.contacts.length" class="mt-3">
              <p class="text-caption text-medium-emphasis mb-1">Contacts</p>
              <div v-for="contact in venue.contacts" :key="contact.id" class="text-body2">
                {{ contact.name }}<span v-if="contact.role" class="text-medium-emphasis"> — {{ contact.role }}</span>
                <span v-if="contact.email || contact.phone" class="text-medium-emphasis">
                  ({{ [contact.email, contact.phone].filter(Boolean).join(', ') }})
                </span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showDialog" width="700">
      <v-card>
        <v-card-title>{{ editingId ? 'Edit Venue' : 'Add Venue' }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="save">
            <v-text-field v-model="form.name" label="Name" required class="mb-3" />
            <v-autocomplete
              v-model="form.timeZone"
              :items="timeZoneOptions"
              label="Time zone"
              hint="Gigs and calls at this venue are shown in this zone"
              persistent-hint
              :rules="[v => !!v || 'Time zone is required']"
              class="mb-3"
            />

            <v-text-field v-model="form.street" label="Street" class="mb-3" />
            <v-row dense class="mb-1">
              <v-col cols="6"><v-text-field v-model="form.city" label="City" /></v-col>
              <v-col cols="3"><v-text-field v-model="form.state" label="State" /></v-col>
              <v-col cols="3"><v-text-field v-model="form.postalCode" label="Zip" /></v-col>
            </v-row>

            <v-textarea v-model="form.notes" label="Notes" rows="2" class="mb-3" />

            <v-divider class="my-4" />
            <div class="d-flex align-center justify-space-between mb-2">
              <p class="text-subtitle-2">Rooms</p>
              <v-btn size="small" variant="tonal" @click="addRoom">
                <v-icon start size="small">mdi-plus</v-icon>Add Room
              </v-btn>
            </div>
            <v-row v-for="(room, i) in form.rooms" :key="i" dense align="center" class="mb-1">
              <v-col cols="5"><v-text-field v-model="room.name" label="Room name" density="compact" hide-details /></v-col>
              <v-col cols="6"><v-text-field v-model="room.notes" label="Notes" density="compact" hide-details /></v-col>
              <v-col cols="1" class="text-right">
                <v-btn icon="mdi-close" size="x-small" variant="text" @click="form.rooms.splice(i, 1)" />
              </v-col>
            </v-row>
            <p v-if="form.rooms.length === 0" class="text-body2 text-medium-emphasis mb-3">No rooms added</p>

            <v-divider class="my-4" />
            <div class="d-flex align-center justify-space-between mb-2">
              <p class="text-subtitle-2">Contacts</p>
              <v-btn size="small" variant="tonal" @click="addContact">
                <v-icon start size="small">mdi-plus</v-icon>Add Contact
              </v-btn>
            </div>
            <div v-for="(contact, i) in form.contacts" :key="i" class="mb-3">
              <v-row dense align="center">
                <v-col cols="5"><v-text-field v-model="contact.name" label="Name" density="compact" hide-details /></v-col>
                <v-col cols="6"><v-text-field v-model="contact.role" label="Role" density="compact" hide-details /></v-col>
                <v-col cols="1" class="text-right">
                  <v-btn icon="mdi-close" size="x-small" variant="text" @click="form.contacts.splice(i, 1)" />
                </v-col>
              </v-row>
              <v-row dense align="center" class="mt-1">
                <v-col cols="6"><v-text-field v-model="contact.email" label="Email" type="email" density="compact" hide-details /></v-col>
                <v-col cols="6"><v-text-field v-model="contact.phone" label="Phone" density="compact" hide-details /></v-col>
              </v-row>
            </div>
            <p v-if="form.contacts.length === 0" class="text-body2 text-medium-emphasis mb-3">No contacts added</p>

            <div class="text-right mt-4">
              <v-btn text @click="showDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit" :loading="saving">Save</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import type { Address, Venue, VenueContact, VenueRoom } from '@gigdriver/domain';
import { listTimeZones } from '@gigdriver/domain';
import { useVenuesStore } from '../stores/venues';
import { useOrganizationStore } from '../stores/organization';

interface ContactRow {
  id: string;
  name: string;
  role: string;
  email: string;
  phone: string;
}

interface RoomRow {
  id: string;
  name: string;
  notes: string;
}

const route = useRoute();
const store = useVenuesStore();
const orgStore = useOrganizationStore();
const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const timeZoneOptions = listTimeZones(browserTimeZone);

const showDialog = ref(false);
const saving = ref(false);
const editingId = ref<string | null>(null);

const form = reactive({
  name: '',
  timeZone: '',
  street: '',
  city: '',
  state: '',
  postalCode: '',
  notes: '',
  contacts: [] as ContactRow[],
  rooms: [] as RoomRow[],
});

const orgId = () => route.query.org as string;

onMounted(() => {
  if (orgId()) store.loadOrgVenues(orgId());
});

function formatAddress(a?: Address): string {
  if (!a) return '';
  const line2 = [a.city, a.state, a.postalCode].filter(Boolean).join(' ');
  return [a.street, line2].filter(Boolean).join(', ');
}

function newId(): string {
  return crypto.randomUUID();
}

function addContact() {
  form.contacts.push({ id: newId(), name: '', role: '', email: '', phone: '' });
}

function addRoom() {
  form.rooms.push({ id: newId(), name: '', notes: '' });
}

function resetForm() {
  form.name = '';
  // New venues default to the org's zone (else the browser's) — change it for venues elsewhere.
  form.timeZone = orgStore.currentOrg?.timezone ?? browserTimeZone;
  form.street = '';
  form.city = '';
  form.state = '';
  form.postalCode = '';
  form.notes = '';
  form.contacts = [];
  form.rooms = [];
}

function openCreate() {
  editingId.value = null;
  resetForm();
  showDialog.value = true;
}

function openEdit(venue: Venue) {
  editingId.value = venue.id;
  form.name = venue.name;
  form.timeZone = venue.timeZone ?? orgStore.currentOrg?.timezone ?? browserTimeZone;
  form.street = venue.address?.street ?? '';
  form.city = venue.address?.city ?? '';
  form.state = venue.address?.state ?? '';
  form.postalCode = venue.address?.postalCode ?? '';
  form.notes = venue.notes ?? '';
  form.contacts = venue.contacts.map(c => ({
    id: c.id,
    name: c.name,
    role: c.role ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
  }));
  form.rooms = venue.rooms.map(r => ({ id: r.id, name: r.name, notes: r.notes ?? '' }));
  showDialog.value = true;
}

async function save() {
  if (!form.name.trim() || !form.timeZone) return;
  try {
    saving.value = true;
    const address: Address = {
      street: form.street || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      postalCode: form.postalCode || undefined,
    };
    const hasAddress = Object.values(address).some(Boolean);
    const contacts: VenueContact[] = form.contacts
      .filter(c => c.name.trim())
      .map(c => ({
        id: c.id,
        name: c.name.trim(),
        role: c.role || undefined,
        email: c.email || undefined,
        phone: c.phone || undefined,
      }));
    const rooms: VenueRoom[] = form.rooms
      .filter(r => r.name.trim())
      .map(r => ({ id: r.id, name: r.name.trim(), notes: r.notes || undefined }));
    const payload = {
      name: form.name,
      timeZone: form.timeZone,
      address: hasAddress ? address : undefined,
      notes: form.notes || undefined,
      contacts,
      rooms,
    };
    if (editingId.value) {
      await store.updateVenue(orgId(), editingId.value, payload);
    } else {
      await store.createVenue(orgId(), payload);
    }
    showDialog.value = false;
  } catch {
    // store.error is set
  } finally {
    saving.value = false;
  }
}

async function removeVenue(id: string) {
  if (!confirm('Delete this venue?')) return;
  await store.deleteVenue(orgId(), id);
}
</script>
